// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import { Modal, AppIcon } from '@loa/ui-components';
import type { StepComponentProps } from '../../types/wizard';
import type { ExtractedDocument, ExtractionRunResult } from '../../types/extraction';

interface StepUploadDocumentsProps extends StepComponentProps {
  /**
   * Wizard-level callback that runs the extractor against the provided
   * files, dispatches field updates across other steps and returns a
   * summary the uploader can render.
   */
  onScanForData: (files: File[]) => Promise<ExtractionRunResult>;
  /**
   * Mark the upload-documents step complete (so the user can continue).
   * Called once the user has scanned at least one PDF *or* clicked
   * "Skip and continue manually".
   */
  onMarkComplete: () => void;
  /**
   * Wizard-level Save & Continue handler. Advances the wizard to the
   * next active step after marking the current step complete.
   */
  onAdvance: () => void;
}

const ANNUAL_FIELD_KEY = 'sorpFile';
const TRANSACTIONS_FIELD_KEY = 'transactionsFile';

export function StepUploadDocuments({
  pendingFiles,
  onAddFile,
  onRemoveFile,
  onScanForData,
  onMarkComplete,
  onAdvance,
}: StepUploadDocumentsProps) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Warnings from the most recent extraction run (e.g. image-based PDFs). */
  const [extractionWarnings, setExtractionWarnings] = useState<string[]>([]);
  // Dialog visibility: 'closed' before scanning, 'scanning' while the
  // extractor runs, 'complete' once it has finished successfully.
  const [dialogState, setDialogState] = useState<'closed' | 'scanning' | 'complete'>('closed');

  const annualFile = pendingFiles?.get(ANNUAL_FIELD_KEY)?.[0];
  const transactionsFile = pendingFiles?.get(TRANSACTIONS_FIELD_KEY)?.[0];

  const handleSelect = useCallback(
    (fieldKey: string, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Replace any existing pending file for this slot
      onRemoveFile?.(fieldKey);
      onAddFile?.(fieldKey, file);
      setError(null);
      e.target.value = '';
    },
    [onAddFile, onRemoveFile],
  );

  const canScan = !!(annualFile || transactionsFile) && !scanning;

  const handleScan = useCallback(async () => {
    const files: File[] = [];
    if (annualFile) files.push(annualFile);
    if (transactionsFile) files.push(transactionsFile);
    if (files.length === 0) return;

    setScanning(true);
    setError(null);
    setDialogState('scanning');
    try {
      const runResult = await onScanForData(files);
      // Mark the step complete as soon as we successfully process anything,
      // even if some documents were UNKNOWN — the user can still continue.
      const anyRecognised = runResult.documents.some(
        (d: ExtractedDocument) => d.kind !== 'UNKNOWN',
      );
      if (anyRecognised || runResult.fieldsPopulated > 0) {
        onMarkComplete();
      }
      setExtractionWarnings(runResult.warnings);
      setDialogState('complete');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We could not read those files. Please try again or continue manually.',
      );
      setDialogState('closed');
    } finally {
      setScanning(false);
    }
  }, [annualFile, transactionsFile, onScanForData, onMarkComplete]);

  const handleSkip = useCallback(() => {
    onMarkComplete();
    onAdvance();
  }, [onMarkComplete, onAdvance]);

  const handleVerifyData = useCallback(() => {
    setDialogState('closed');
    onAdvance();
  }, [onAdvance]);

  const handleCloseDialog = useCallback(() => {
    if (scanning) return; // don't allow closing mid-scan
    setDialogState('closed');
  }, [scanning]);

  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Upload documents
      </div>

      <p className="text-grey-04 text-body-03 font-sans">
        If you have a Statement of Reasonable Projection or a transactions
        statement for this policy, upload them here and we will pre-fill as
        much of the form as we can. You will still be able to edit any field.
      </p>

      <UploadSlot
        label="Statement of Reasonable Projection"
        fieldKey={ANNUAL_FIELD_KEY}
        file={annualFile}
        onSelect={handleSelect}
        onClear={() => {
          onRemoveFile?.(ANNUAL_FIELD_KEY);
            }}
      />

      <div className="w-full h-[1px] bg-[#E1E1E1]" />

      <UploadSlot
        label="Transactions statement"
        fieldKey={TRANSACTIONS_FIELD_KEY}
        file={transactionsFile}
        onSelect={handleSelect}
        onClear={() => {
          onRemoveFile?.(TRANSACTIONS_FIELD_KEY);
            }}
      />

      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-md">
        <button
          type="button"
          onClick={handleSkip}
          disabled={scanning}
          className="text-purple-primary font-sans text-body-02 font-medium px-4 py-2 hover:underline disabled:opacity-50"
        >
          Skip and continue manually
        </button>
        <button
          type="button"
          onClick={handleScan}
          disabled={!canScan}
          className={`bg-purple-primary text-white font-sans text-body-02 font-medium capitalize h-[48px] px-xl rounded-button transition-opacity ${
            canScan ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          {scanning ? 'Scanning…' : 'Scan for data'}
        </button>
      </div>

      <Modal open={dialogState === 'scanning'} onClose={handleCloseDialog} size="small" closeOnOverlayClick={false} closeOnEscape={false}>
        <Modal.Header title="Scanning documents" onClose={handleCloseDialog} />
        <Modal.Content>
          <div className="flex flex-col items-center justify-center gap-md py-sm">
            <p className="text-[#333] font-sans text-body-02 text-center">
              Please wait while we scan the document for extractable data. This should take 30 seconds.
            </p>
            <div className="w-[40px] h-[40px] border-[3px] border-[#E1E1E1] border-t-purple-primary rounded-full animate-spin" />
          </div>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{
            label: 'Cancel',
            onClick: handleCloseDialog,
            disabled: scanning,
          }}
        />
      </Modal>

      <Modal open={dialogState === 'complete'} onClose={handleCloseDialog} size="small">
        <Modal.Header
          icon={extractionWarnings.length > 0 ? 'danger' : 'check circle'}
          title={extractionWarnings.length > 0 ? 'Scan complete — with warnings' : 'Scan complete'}
          onClose={handleCloseDialog}
        />
        <Modal.Content>
          <div className="flex flex-col gap-md">
            {extractionWarnings.length > 0 ? (
              <>
                {extractionWarnings.map((w) => (
                  <div
                    key={w}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                    role="alert"
                  >
                    {w}
                  </div>
                ))}
                <p className="text-[#333] font-sans text-body-02">
                  Some documents could not be auto-filled. Please fill in the remaining fields manually.
                </p>
              </>
            ) : (
              <>
                <p className="text-[#333] font-sans text-body-02">
                  The data has been auto-filled. Please manually fill in any outstanding information
                </p>
                <p className="text-[#333] font-sans text-body-02">Example:</p>
                <div className="flex items-center gap-sm">
                  <AppIcon name="checkbox" size={24} color="#00A63E" />
                  <span className="text-[#333] font-sans text-body-02">
                    = Data has been auto-filled correctly
                  </span>
                </div>
                <div className="flex items-center gap-sm">
                  <AppIcon name="danger" size={24} color="#96040E" />
                  <span className="text-[#333] font-sans text-body-02">
                    = Data needs to be manually filled
                  </span>
                </div>
              </>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: handleCloseDialog }}
          primaryAction={{
            label: extractionWarnings.length > 0 ? 'Continue manually' : 'Verify Data',
            onClick: handleVerifyData,
          }}
        />
      </Modal>
    </div>
  );
}

interface UploadSlotProps {
  label: string;
  fieldKey: string;
  file: File | undefined;
  onSelect: (fieldKey: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}

function UploadSlot({ label, fieldKey, file, onSelect, onClear }: UploadSlotProps) {
  const inputId = `upload-slot-${fieldKey}`;
  return (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium">
        {label}
      </div>

      {!file ? (
        <>
          <label
            htmlFor={inputId}
            className="border border-purple-primary text-purple-primary font-sans text-body-02 font-medium capitalize h-[48px] px-xl rounded-button inline-flex items-center justify-center cursor-pointer self-start hover:bg-lilac-05 transition-colors"
          >
            Upload document
          </label>
          <input
            id={inputId}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(e) => onSelect(fieldKey, e)}
          />
          <div className="text-grey-04 font-sans text-body-03">
            Accepted file type: PDF
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-black font-sans text-body-03 truncate max-w-[280px]">
            {file.name}
          </span>
          <span className="text-grey-04 text-body-04">
            ({(file.size / (1024 * 1024)).toFixed(2)} MB)
          </span>
          <button
            type="button"
            onClick={onClear}
            className="text-purple-primary text-body-03 underline"
            aria-label={`Remove ${file.name}`}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
